import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Phone, Mail, MapPin, Instagram, Facebook, Twitter, Music2, Send, Clock } from "lucide-react";
import { CONTACT, SEO_PAGES } from "@/data/site";
import { Eyebrow } from "@/components/Shared";
import { Seo } from "@/components/Seo";

const socialMap = { instagram: Instagram, facebook: Facebook, twitter: Twitter, music: Music2 };

const CHANNELS = [
  { icon: MessageCircle, label: "WhatsApp", value: CONTACT.whatsapp, href: CONTACT.whatsappOrderHref, testid: "contact-whatsapp", accent: "bg-[#25D366]" },
  { icon: Phone, label: "Call Us", value: CONTACT.phone, href: CONTACT.phoneHref, testid: "contact-phone", accent: "bg-brand-green" },
  { icon: Mail, label: "Email", value: CONTACT.email, href: CONTACT.emailHref, testid: "contact-email", accent: "bg-brand-purple" },
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }

    const subject = encodeURIComponent(form.subject || "Website enquiry");
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
    );
    window.location.href = `${CONTACT.emailHref}?subject=${subject}&body=${body}`;
    toast.success("Your email app is opening with the message ready to send.");
  };

  return (
    <div data-testid="contact-page">
      <Seo title={SEO_PAGES.contact.title} description={SEO_PAGES.contact.description} />
      <section className="pt-36 lg:pt-44 pb-12 text-center container-px">
        <div className="mx-auto max-w-2xl animate-fade-up">
          <Eyebrow>Contact</Eyebrow>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-ink">
            Let's talk freshness
          </h1>
          <p className="mt-6 text-base md:text-lg text-brand-slate leading-relaxed">
            Orders, catering enquiries or partnerships — we'd love to hear from
            you. WhatsApp is the quickest way to reach our team.
          </p>
        </div>
      </section>

      {/* Channels */}
      <section className="pb-12 container-px">
        <div className="mx-auto max-w-[1400px] grid gap-5 sm:grid-cols-3">
          {CHANNELS.map((c) => (
            <a
              key={c.label}
              href={c.href}
              target={c.label === "Call Us" ? undefined : "_blank"}
              rel="noopener noreferrer"
              data-testid={c.testid}
              className="group rounded-3xl border border-black/5 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <div className={`grid h-14 w-14 place-items-center rounded-2xl text-white ${c.accent}`}>
                <c.icon size={26} />
              </div>
              <h3 className="mt-5 text-lg font-medium text-brand-ink">{c.label}</h3>
              <p className="mt-1 text-brand-slate">{c.value}</p>
            </a>
          ))}
        </div>
      </section>

      {/* Form + info */}
      <section className="section-py">
        <div className="container-px mx-auto max-w-[1400px] grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-ink">Send us a message</h2>
            <p className="mt-3 text-brand-slate">Fill in the form and we'll get back to you shortly.</p>
            <form onSubmit={onSubmit} data-testid="contact-form" className="mt-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-medium text-brand-ink">Name</label>
                  <input data-testid="contact-input-name" value={form.name} onChange={update("name")} placeholder="Your name" className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3.5 text-brand-ink outline-none transition-colors focus:border-brand-green" />
                </div>
                <div>
                  <label className="text-sm font-medium text-brand-ink">Email</label>
                  <input data-testid="contact-input-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3.5 text-brand-ink outline-none transition-colors focus:border-brand-green" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-brand-ink">Subject</label>
                <input data-testid="contact-input-subject" value={form.subject} onChange={update("subject")} placeholder="How can we help?" className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3.5 text-brand-ink outline-none transition-colors focus:border-brand-green" />
              </div>
              <div>
                <label className="text-sm font-medium text-brand-ink">Message</label>
                <textarea data-testid="contact-input-message" value={form.message} onChange={update("message")} rows={5} placeholder="Tell us a little more..." className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3.5 text-brand-ink outline-none transition-colors focus:border-brand-green resize-none" />
              </div>
              <button type="submit" data-testid="contact-submit" className="inline-flex items-center gap-2 rounded-full bg-brand-green px-8 py-4 text-sm font-medium text-white transition-all duration-300 hover:bg-brand-greenDark hover:-translate-y-0.5">
                <Send size={17} /> Send Message
              </button>
            </form>
          </div>

          <div className="rounded-[2rem] bg-brand-greenDark text-white p-10 lg:p-12">
            <h3 className="text-2xl font-semibold">Visit & connect</h3>
            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-4">
                <span className="grid h-11 w-11 place-items-center rounded-full bg-white/10 shrink-0"><MapPin size={20} /></span>
                <div>
                  <p className="font-medium">Location</p>
                  <p className="text-white/70">{CONTACT.address}</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="grid h-11 w-11 place-items-center rounded-full bg-white/10 shrink-0"><MessageCircle size={20} /></span>
                <div>
                  <p className="font-medium">WhatsApp</p>
                  <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors">{CONTACT.whatsapp}</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="grid h-11 w-11 place-items-center rounded-full bg-white/10 shrink-0"><Phone size={20} /></span>
                <div>
                  <p className="font-medium">Phone</p>
                  <a href={CONTACT.phoneHref} className="block text-white/70 hover:text-white transition-colors">{CONTACT.phone}</a>
                  <a href={CONTACT.phone2Href} className="block text-white/70 hover:text-white transition-colors">{CONTACT.phone2}</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="grid h-11 w-11 place-items-center rounded-full bg-white/10 shrink-0"><Mail size={20} /></span>
                <div>
                  <p className="font-medium">Email</p>
                  <a href={CONTACT.emailHref} className="text-white/70 hover:text-white transition-colors">{CONTACT.email}</a>
                </div>
              </li>
            </ul>

            {CONTACT.socials.length > 0 && (
              <div className="mt-10 pt-8 border-t border-white/10">
                <p className="font-medium mb-4">Follow Us</p>
                <div className="flex flex-col gap-3">
                  {CONTACT.socials.map((s) => {
                    const Icon = socialMap[s.icon] || Instagram;
                    return (
                      <a key={s.name} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={`contact-social-${s.name.toLowerCase()}`} className="flex items-center gap-3 text-white/75 hover:text-white transition-colors group">
                        <span className="grid h-11 w-11 place-items-center rounded-full border border-white/20 transition-all duration-300 group-hover:bg-white group-hover:text-brand-greenDark">
                          <Icon size={18} />
                        </span>
                        <span><span className="text-white">{s.name}</span> · {s.handle}</span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Business hours */}
      <section data-testid="business-hours" className="pb-20 lg:pb-28">
        <div className="container-px mx-auto max-w-[1400px]">
          <div className="rounded-[2rem] bg-brand-alt border border-black/5 p-8 lg:p-12 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-center gap-3">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-mist text-brand-greenDark"><Clock size={24} /></span>
                <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-ink">Business Hours</h2>
              </div>
              <p className="mt-5 text-brand-slate leading-relaxed max-w-md">
                Orders and enquiries are welcome during business hours. WhatsApp
                messages may be sent at any time.
              </p>
            </div>
            <ul data-testid="contact-hours" className="divide-y divide-black/5">
              {CONTACT.hours.map((h) => (
                <li key={h.day} className="flex items-center justify-between py-3.5">
                  <span className="font-medium text-brand-ink">{h.day}</span>
                  <span className="text-brand-slate">{h.time}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
